/**
 * Move Muse — Wishlist (localStorage, sans app tierce)
 *
 * - <mm-wishlist-toggle>  : cœur sur les cartes produit (toggle add/remove)
 * - <mm-wishlist-header>  : icône cœur du header (compteur + ouverture du drawer)
 * - <mm-wishlist-drawer>  : drawer listant les favoris
 *
 * Stockage : localStorage 'mm_wishlist' = [{ handle, url, title, price, image }]
 * Événement global : 'mm:wishlist:change' (window) à chaque modification.
 */

const STORAGE_KEY = 'mm_wishlist';
const CHANGE_EVENT = 'mm:wishlist:change';

function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    return Array.isArray(items) ? items.filter((item) => item && item.handle) : [];
  } catch {
    return [];
  }
}

function writeWishlist(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* stockage indisponible (navigation privée…) : on continue en mémoire */
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { items } }));
}

function inWishlist(handle) {
  return readWishlist().some((item) => item.handle === handle);
}

function toggleWishlist(item) {
  const items = readWishlist();
  const index = items.findIndex((existing) => existing.handle === item.handle);
  if (index >= 0) {
    items.splice(index, 1);
  } else {
    items.push(item);
  }
  writeWishlist(items);
  return index < 0;
}

function removeFromWishlist(handle) {
  writeWishlist(readWishlist().filter((item) => item.handle !== handle));
}

/* ------------------------------------------------------------------ */
/* Cœur sur les cartes                                                 */
/* ------------------------------------------------------------------ */
class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;

    this.sync = this.sync.bind(this);
    this.onClick = this.onClick.bind(this);

    this.button.addEventListener('click', this.onClick);
    window.addEventListener(CHANGE_EVENT, this.sync);
    window.addEventListener('storage', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.sync);
    window.removeEventListener('storage', this.sync);
  }

  onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    toggleWishlist({
      handle: this.dataset.handle,
      url: this.dataset.url,
      title: this.dataset.title,
      price: this.dataset.price,
      image: this.dataset.image,
    });
  }

  sync() {
    const active = inWishlist(this.dataset.handle);
    this.classList.toggle('is-active', active);
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
  }
}

/* ------------------------------------------------------------------ */
/* Icône header + compteur                                             */
/* ------------------------------------------------------------------ */
class WishlistHeader extends HTMLElement {
  connectedCallback() {
    this.countEl = this.querySelector('[data-wishlist-count]');
    this.sync = this.sync.bind(this);

    this.querySelector('button')?.addEventListener('click', () => {
      document.querySelector('mm-wishlist-drawer')?.open();
    });

    window.addEventListener(CHANGE_EVENT, this.sync);
    window.addEventListener('storage', this.sync);
    this.sync();
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.sync);
    window.removeEventListener('storage', this.sync);
  }

  sync() {
    const count = readWishlist().length;
    if (!this.countEl) return;
    this.countEl.hidden = count === 0;
    this.countEl.textContent = count > 99 ? '99+' : String(count);
  }
}

/* ------------------------------------------------------------------ */
/* Drawer favoris                                                      */
/* ------------------------------------------------------------------ */
class WishlistDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.listEl = this.querySelector('[data-wishlist-items]');
    this.emptyEl = this.querySelector('[data-wishlist-empty]');
    this.render = this.render.bind(this);

    this.dialog?.addEventListener('click', (event) => {
      // Clic sur le backdrop → fermeture
      if (event.target === this.dialog) this.close();
    });
    this.dialog?.addEventListener('close', () => {
      document.documentElement.style.removeProperty('overflow');
    });
    this.querySelector('[data-wishlist-close]')?.addEventListener('click', () => this.close());

    this.listEl?.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        removeFromWishlist(removeButton.dataset.wishlistRemove);
        return;
      }

      const addButton = event.target.closest('[data-wishlist-add]');
      if (addButton) {
        event.preventDefault();
        this.handleAddToCart(addButton, event);
      }
    });

    window.addEventListener(CHANGE_EVENT, this.render);
    window.addEventListener('storage', this.render);
  }

  disconnectedCallback() {
    window.removeEventListener(CHANGE_EVENT, this.render);
    window.removeEventListener('storage', this.render);
  }

  open() {
    if (!this.dialog) return;
    this.render();
    this.dialog.showModal();
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  close() {
    this.dialog?.close();
  }

  /**
   * "Ajouter au panier" : délègue au quick-add NATIF Horizon (même mécanique que
   * le "+ Ajouter" du Complétez le look) — produit à variantes → modal
   * couleur/taille, jamais d'ajout d'une variante par défaut. Le produit reste
   * dans la wishlist. Le drawer favoris se ferme pour laisser place au modal,
   * puis au drawer panier (auto-open natif après l'ajout).
   */
  handleAddToCart(addButton, event) {
    const quickAdd = addButton.closest('quick-add-component');
    if (quickAdd && typeof quickAdd.handleClick === 'function') {
      this.close();
      this.armCartDrawerOpen();
      quickAdd.handleClick(event);
      return;
    }
    // Secours (composant natif indisponible) : navigation vers la fiche produit
    const url = addButton.closest('[data-wishlist-item]')?.querySelector('a')?.href;
    if (url) window.location.href = url;
  }

  /**
   * Arme l'ouverture du drawer panier pour CE flux uniquement : le réglage
   * global auto_open_cart_drawer reste inchangé. Désarmé si le modal
   * quick-add est refermé sans ajout.
   */
  armCartDrawerOpen() {
    const controller = new AbortController();

    document.addEventListener(
      'cart:update',
      (event) => {
        controller.abort();
        if (event.detail?.data?.didError) return;
        document.querySelector('cart-drawer-component')?.open?.();
      },
      { signal: controller.signal }
    );

    // 'cart:update' est émis AVANT la fermeture du modal : le délai évite de
    // désarmer le listener avant qu'il n'ait traité l'ajout.
    document.querySelector('#quick-add-dialog dialog')?.addEventListener(
      'close',
      () => setTimeout(() => controller.abort(), 100),
      { once: true }
    );
  }

  render() {
    if (!this.listEl || !this.emptyEl) return;
    const items = readWishlist();

    this.emptyEl.hidden = items.length > 0;
    this.listEl.hidden = items.length === 0;

    const addLabel = this.dataset.addLabel || 'Ajouter au panier';

    this.listEl.innerHTML = items
      .map(
        (item) => `
          <li class="mm-wishlist-drawer__item" data-wishlist-item>
            <a class="mm-wishlist-drawer__media" href="${escapeAttribute(item.url)}" tabindex="-1">
              ${item.image ? `<img src="${escapeAttribute(item.image)}" alt="" loading="lazy" width="140" height="175">` : ''}
            </a>
            <div class="mm-wishlist-drawer__info">
              <a class="mm-wishlist-drawer__title" href="${escapeAttribute(item.url)}">${escapeHtml(item.title)}</a>
              <span class="mm-wishlist-drawer__price">${escapeHtml(item.price)}</span>
              <product-card class="mm-wishlist-drawer__quick-add" data-no-navigation>
                <a class="visually-hidden" href="${escapeAttribute(item.url)}" ref="productCardLink" tabindex="-1" aria-hidden="true">${escapeHtml(item.title)}</a>
                <quick-add-component
                  ref="quickAdd"
                  data-product-title="${escapeAttribute(item.title)}"
                  data-quick-add-button="choose"
                  data-product-options-count="2"
                >
                  <button type="button" class="mm-wishlist-drawer__add" data-wishlist-add>${escapeHtml(addLabel)}</button>
                </quick-add-component>
              </product-card>
            </div>
            <button
              type="button"
              class="mm-wishlist-drawer__remove"
              data-wishlist-remove="${escapeAttribute(item.handle)}"
              aria-label="${this.dataset.removeLabel || 'Retirer'}"
            >&times;</button>
          </li>`
      )
      .join('');
  }
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeAttribute(value) {
  return escapeHtml(value).replaceAll('"', '&quot;');
}

if (!customElements.get('mm-wishlist-toggle')) customElements.define('mm-wishlist-toggle', WishlistToggle);
if (!customElements.get('mm-wishlist-header')) customElements.define('mm-wishlist-header', WishlistHeader);
if (!customElements.get('mm-wishlist-drawer')) customElements.define('mm-wishlist-drawer', WishlistDrawer);
